import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import Layout from './components/Layout';
import FeatureList        from './pages/features/FeatureList';
import FeatureForm        from './pages/features/FeatureForm';
import FeatureDetail      from './pages/features/FeatureDetail';
import SheetImportWizard  from './pages/features/SheetImportWizard';
import PRImportWizard     from './pages/features/PRImportWizard';
import FileFeaturesPage   from './pages/features/FileFeaturesPage';
import AISettingsPage     from './pages/settings/AISettingsPage';
import ProductList    from './pages/products/ProductList';
import ProductForm    from './pages/products/ProductForm';
import ProductDetail  from './pages/products/ProductDetail';
import JobsList            from './pages/jobs/JobsList';
import JobDetail           from './pages/jobs/JobDetail';
import RepositoriesList    from './pages/repositories/RepositoriesList';
import ProvidersConfig     from './pages/repositories/ProvidersConfig';
import RepoOwners          from './pages/repositories/RepoOwners';
import BlastRadiusPage     from './pages/blast_radius/BlastRadiusPage';
import Dashboard            from './pages/dashboard/Dashboard';
import McpConfigPage        from './pages/mcp/McpConfigPage';
import ReviewConfigsList    from './pages/code_review/ReviewConfigsList';
import ReviewConfigForm     from './pages/code_review/ReviewConfigForm';
import ReviewRunsList       from './pages/code_review/ReviewRunsList';
import ReviewRunDetail      from './pages/code_review/ReviewRunDetail';
import ReviewCosts          from './pages/code_review/ReviewCosts';
import ReviewRules          from './pages/code_review/ReviewRules';
import ReviewPrompts        from './pages/code_review/ReviewPrompts';
import MonitorReport        from './pages/code_review/MonitorReport';
import DeveloperQuality     from './pages/code_review/DeveloperQuality';
import DeveloperQualityDetail from './pages/code_review/DeveloperQualityDetail';
import SecretsDashboard, { SecretsActivity } from './pages/secrets/SecretsDashboard';
import SecretKeysList       from './pages/secrets/SecretKeysList';
import SecretKeyDetail      from './pages/secrets/SecretKeyDetail';
import { SecretReposList, SecretRepoDetail } from './pages/secrets/SecretRepos';
import SecretsSharing       from './pages/secrets/SecretsSharing';
import SecretGone           from './pages/secrets/SecretGone';
import SecretRules          from './pages/secrets/SecretRules';
import { SecretsAccessContext, resetSecretsAccess, useSecretsAccessLoader } from './pages/secrets/common';
import RiskRunsList         from './pages/risk_analysis/RiskRunsList';
import RiskRunDetail        from './pages/risk_analysis/RiskRunDetail';
import RiskDocs             from './pages/risk_analysis/RiskDocs';
import ApiKeysPage          from './pages/settings/ApiKeysPage';
import FleetHosts           from './pages/cve_fleet/FleetHosts';
import FleetHostDetail      from './pages/cve_fleet/FleetHostDetail';
import FleetCampaigns       from './pages/cve_fleet/FleetCampaigns';
import FleetCampaignDetail  from './pages/cve_fleet/FleetCampaignDetail';
import FleetEnrollment      from './pages/cve_fleet/FleetEnrollment';
import FleetAudit           from './pages/cve_fleet/FleetAudit';
import { canViewDeveloperQuality, canManageApiKeys } from './access';

// ── Auth context ──────────────────────────────────────────────────────────────
export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem('pf_user');
    return raw ? JSON.parse(raw) : null;
  });

  function login(token, profile) {
    resetSecretsAccess();
    localStorage.setItem('pf_token', token);
    localStorage.setItem('pf_user', JSON.stringify(profile));
    setUser(profile);
  }

  function logout() {
    resetSecretsAccess();
    localStorage.removeItem('pf_token');
    localStorage.removeItem('pf_user');
    setUser(null);
  }

  // The axios response interceptor fires this on any 401 (expired/invalid token).
  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener('pf:unauthorized', onUnauthorized);
    return () => window.removeEventListener('pf:unauthorized', onUnauthorized);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Protected route ───────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  // Remember where the session dropped so login can send the user back.
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

// ── Restricted route ──────────────────────────────────────────────────────────
// Pages limited to named users. The API enforces the same list; this just keeps
// the URL from rendering a page the caller would only get 403s on.
function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!canManageApiKeys(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

function DeveloperQualityRoute({ children }) {
  const { user } = useAuth();
  if (!canViewDeveloperQuality(user)) return <Navigate to="/code-review" replace />;
  return children;
}

// Secrets rotation tracker: the owner plus whoever the owner shared it with.
// The API enforces the same list; this only avoids rendering a page of 403s.
function SecretsRoute({ children, ownerOnly = false }) {
  const { loading, access } = useSecretsAccessLoader();
  if (loading) return <div className="page-body"><span className="spinner" /></div>;
  if (!access || (ownerOnly && !access.is_owner)) return <Navigate to="/dashboard" replace />;
  return <SecretsAccessContext.Provider value={access}>{children}</SecretsAccessContext.Provider>;
}

// ── App root ──────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="home" element={<Navigate to="/dashboard" replace />} />
                    <Route path="features"                element={<FeatureList />} />
                    <Route path="features/new"            element={<FeatureForm />} />
                    <Route path="features/import"         element={<SheetImportWizard />} />
                    <Route path="features/import/pr"     element={<PRImportWizard />} />
                    <Route path="features/file-mapping"  element={<FileFeaturesPage />} />
                    <Route path="features/:id"            element={<FeatureDetail />} />
                    <Route path="features/:id/edit"       element={<FeatureForm />} />
                    <Route path="products"           element={<ProductList />} />
                    <Route path="products/new"       element={<ProductForm />} />
                    <Route path="products/:id"       element={<ProductDetail />} />
                    <Route path="products/:id/edit"  element={<ProductForm />} />
                    <Route path="jobs"               element={<JobsList />} />
                    <Route path="jobs/:id"           element={<JobDetail />} />
                    <Route path="repositories"           element={<RepositoriesList />} />
                    <Route path="repositories/providers" element={<ProvidersConfig />} />
                    <Route path="repo-owners"            element={<RepoOwners />} />
                    <Route path="blast-radius"           element={<BlastRadiusPage />} />
                    <Route path="code-review"               element={<ReviewConfigsList />} />
                    <Route path="code-review/configs/new"   element={<ReviewConfigForm />} />
                    <Route path="code-review/configs/:id/edit" element={<ReviewConfigForm />} />
                    <Route path="code-review/runs"          element={<ReviewRunsList />} />
                    <Route path="code-review/runs/:id"      element={<ReviewRunDetail />} />
                    <Route path="code-review/costs"         element={<ReviewCosts />} />
                    <Route path="code-review/rules"         element={<ReviewRules />} />
                    <Route path="code-review/prompts"       element={<ReviewPrompts />} />
                    <Route path="code-review/monitor-report" element={<MonitorReport />} />
                    <Route path="code-review/developer-quality"
                           element={<DeveloperQualityRoute><DeveloperQuality /></DeveloperQualityRoute>} />
                    <Route path="code-review/developer-quality/detail"
                           element={<DeveloperQualityRoute><DeveloperQualityDetail /></DeveloperQualityRoute>} />
                    <Route path="secrets"            element={<SecretsRoute><SecretsDashboard /></SecretsRoute>} />
                    <Route path="secrets/keys"       element={<SecretsRoute><SecretKeysList /></SecretsRoute>} />
                    <Route path="secrets/keys/:id"   element={<SecretsRoute><SecretKeyDetail /></SecretsRoute>} />
                    <Route path="secrets/repos"      element={<SecretsRoute><SecretReposList /></SecretsRoute>} />
                    <Route path="secrets/repos/:id"  element={<SecretsRoute><SecretRepoDetail /></SecretsRoute>} />
                    <Route path="secrets/gone"       element={<SecretsRoute><SecretGone /></SecretsRoute>} />
                    <Route path="secrets/rules"      element={<SecretsRoute><SecretRules /></SecretsRoute>} />
                    <Route path="secrets/activity"   element={<SecretsRoute><SecretsActivity /></SecretsRoute>} />
                    <Route path="secrets/sharing"    element={<SecretsRoute ownerOnly><SecretsSharing /></SecretsRoute>} />
                    <Route path="dashboard"              element={<Dashboard />} />
                    <Route path="mcp-config"             element={<McpConfigPage />} />
                    <Route path="settings/ai"            element={<AISettingsPage />} />
                    <Route path="settings/api-keys"      element={<AdminRoute><ApiKeysPage /></AdminRoute>} />
                    <Route path="risk-analysis"          element={<RiskRunsList />} />
                    <Route path="risk-analysis/docs"     element={<RiskDocs />} />
                    <Route path="risk-analysis/runs/:id" element={<RiskRunDetail />} />
                    <Route path="cve-fleet"                element={<FleetHosts />} />
                    <Route path="cve-fleet/hosts/:cn"      element={<FleetHostDetail />} />
                    <Route path="cve-fleet/campaigns"      element={<FleetCampaigns />} />
                    <Route path="cve-fleet/campaigns/:id"  element={<FleetCampaignDetail />} />
                    <Route path="cve-fleet/enrollment"     element={<FleetEnrollment />} />
                    <Route path="cve-fleet/audit"          element={<FleetAudit />} />
                    <Route path="*" element={<Navigate to="/home" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
